import React, { useState, useEffect } from 'react';
import T from 'i18n';
import { Popover, Box, TextField, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import { MAX_PROMPT_PLACEHOLDERS_ENTRIES } from './constants';

interface PromptPlaceholderInputProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  type: 'claimant' | 'respondent' | 'witness' | 'expert' | 'topic' | 'party';
}

export default function PromptPlaceholderInput({
  anchorEl,
  isOpen,
  onClose,
  value,
  onChange,
  type,
}: PromptPlaceholderInputProps) {
  const [localValue, setLocalValue] = useState<string>(value);
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const caseId = useSelector(selectCurrentCaseId);

  useEffect(() => {
    setLocalValue(value);
  }, [value, isOpen]);

  const handleSave = () => {
    onChange(localValue);

    // Get existing promptPlaceholders array or initialize empty array
    const existingArray = userSettings.aiSettings?.promptPlaceholders || [];

    // Find if there's an entry for the current caseId
    const caseIndex = existingArray.findIndex((entry: any) => entry.caseId === caseId);

    // Create updated array
    let updatedArray;
    if (caseIndex >= 0) {
      // Update existing entry for this case
      updatedArray = [...existingArray];
      updatedArray[caseIndex] = {
        ...updatedArray[caseIndex],
        [type]: { text: localValue },
      };
    } else {
      // Add new entry for this case
      const newEntry = {
        caseId: caseId,
        [type]: { text: localValue },
      };

      // Check if we've reached the maximum limit
      if (existingArray.length >= MAX_PROMPT_PLACEHOLDERS_ENTRIES) {
        // Remove the oldest entry (first element) and add new entry at the end
        updatedArray = [...existingArray.slice(1), newEntry];
      } else {
        // Simply add the new entry
        updatedArray = [...existingArray, newEntry];
      }
    }

    // Save to user settings API
    updateUserSettings({
      userSettings: {
        ...userSettings,
        aiSettings: {
          ...userSettings.aiSettings,
          promptPlaceholders: updatedArray,
        },
      },
    });

    onClose();
  };

  const handleCancel = () => {
    setLocalValue(value); // Reset to original value
    onClose();
  };

  const placeholderMap: Record<string, string> = {
    claimant: T.translate('generic.claimant'),
    respondent: T.translate('generic.respondent'),
    witness: T.translate('generic.witness') || 'Witness',
    expert: T.translate('generic.expert') || 'Expert',
    topic: T.translate('generic.topic') || 'Topic',
    party: T.translate('generic.party') || 'Party',
  };

  const placeholder = placeholderMap[type] || '';

  // Only disable if both current value is empty AND original value was also empty
  const isSaveDisabled = !localValue.trim() && !value.trim();

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={handleCancel}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box sx={{ p: 2, minWidth: 300 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            }
          }}
          autoFocus
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={handleCancel} variant="outlined">
            {T.translate('generic.cancel')}
          </Button>
          <Button
            size="small"
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaveDisabled}
          >
            {T.translate('generic.save')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
